// components/CRM/EtapaWorkflow/PropuestaBuilder.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Tabs,
  Tab,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Grid
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Inventory as InventoryIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { cargarPartidasEstandar, cargarCategoriasPartida } from '../../../services/crmWorkflowService';
import { cargarMaterialesInventario } from '../../../services/crmService';

// ── Datos SaaS (mismos que NecesidadValidadaForm) ─────────────────────────────
const PLANES_SAAS = [
  { label: 'Comercial Starter', precio: 60 },
  { label: 'Comercial Pro',     precio: 120 },
  { label: 'Comercial Scale',   precio: 180 },
  { label: 'Constructor Starter', precio: 99 },
  { label: 'Constructor Pro',     precio: 150 },
  { label: 'Constructor Scale',   precio: 199 },
  { label: 'Contable Starter',  precio: 60 },
  { label: 'Contable Pro',      precio: 99 },
  { label: 'Contable Scale',    precio: 149 },
  { label: 'Renta-car Starter', precio: 89 },
  { label: 'Renta-car Pro',     precio: 130 },
  { label: 'Renta-car Scale',   precio: 179 },
];

const ADDONS_SAAS = [
  { label: 'e-CF',            codigo: 'ecf',             precio: 30 },
  { label: 'CRM',             codigo: 'crm',             precio: 15 },
  { label: 'Multi-empresa',   codigo: 'multi_empresa',   precio: 25 },
  { label: 'Email Marketing', codigo: 'email_marketing', precio: 30 },
];

// ── Subcomponente PropuestaSaaS ────────────────────────────────────────────────
const PropuestaSaas = ({ oportunidad, onGuardado, onTransicionCompleta }) => {
  // Leer datos precargados (BD usa columna necesidad_detalle)
  const nd = oportunidad?.necesidad_detalle || oportunidad?.necesidad_validada || {};

  const planInteres  = nd.plan_interes  || '';
  const addonsInteres = Array.isArray(nd.addons_interes) ? nd.addons_interes : [];

  const planObj    = PLANES_SAAS.find(p => p.label === planInteres);
  const precioBase = planObj?.precio || 0;

  const addonsSeleccionados = ADDONS_SAAS.filter(a => addonsInteres.includes(a.codigo));
  const totalAddons = addonsSeleccionados.reduce((s, a) => s + a.precio, 0);
  const totalMensual = precioBase + totalAddons;
  const totalAnual   = totalMensual * 10;

  const [descuentoTipo,    setDescuentoTipo]    = useState('porcentaje');
  const [descuentoValor,   setDescuentoValor]   = useState('');
  const [ciclo,            setCiclo]            = useState('mensual');
  const [notas,            setNotas]            = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [guardando,        setGuardando]        = useState(false);

  const baseSegunCiclo = ciclo === 'anual' ? totalAnual : totalMensual;

  const descuentoMonto = useMemo(() => {
    const v = parseFloat(descuentoValor) || 0;
    if (!v) return 0;
    return descuentoTipo === 'porcentaje'
      ? Math.round(baseSegunCiclo * (v / 100) * 100) / 100
      : Math.min(v, baseSegunCiclo);
  }, [descuentoValor, descuentoTipo, baseSegunCiclo]);

  const totalConDescuento = Math.round((baseSegunCiclo - descuentoMonto) * 100) / 100;

  const handleGuardar = async () => {
    if (!planInteres) {
      toast.warning('La oportunidad no tiene un plan de interés registrado en Necesidad Validada');
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        oportunidadId: oportunidad.id,
        propuesta_saas: {
          plan_interes:       planInteres,
          precio_base_mensual: precioBase,
          addons_interes:     addonsInteres,
          total_mensual:      totalMensual,
          total_anual:        totalAnual,
          descuento_tipo:     descuentoTipo,
          descuento_valor:    parseFloat(descuentoValor) || 0,
          descuento_monto:    descuentoMonto,
          ciclo_facturacion:  ciclo,
          total_con_descuento: totalConDescuento,
          notas,
          fecha_vencimiento:  fechaVencimiento,
          fecha_envio:        new Date(),
          estado_envio:       'enviado',
          tipo_cotizacion:    'saas',
          version: (oportunidad.propuesta?.version || 0) + 1,
        }
      };

      await onGuardado(payload);
      toast.success('✅ Propuesta SaaS guardada');
      if (onTransicionCompleta) onTransicionCompleta();
    } catch (err) {
      console.error('Error guardando propuesta SaaS:', err);
      toast.error(err.response?.data?.error || 'Error al guardar la propuesta');
    } finally {
      setGuardando(false);
    }
  };

  const fmtUSD = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
        🚀 Propuesta SaaS: {oportunidad.prospecto?.nombre || oportunidad.prospecto?.empresa}
      </Typography>
      <Chip label="SaaS — eFIT" color="info" sx={{ mb: 2 }} />

      {!planInteres && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se encontró plan de interés en la etapa anterior. Regresa a <strong>Necesidad Validada</strong> y selecciona un plan.
        </Alert>
      )}

      {/* ── Resumen del plan ──────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          📋 Resumen de la propuesta
        </Typography>

        <Grid container spacing={1}>
          {/* Plan base */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2">
                Plan: <strong>{planInteres || '—'}</strong>
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {fmtUSD(precioBase)}/mes
              </Typography>
            </Box>
          </Grid>

          {/* Add-ons */}
          {addonsSeleccionados.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary">Add-ons</Typography>
              </Grid>
              {addonsSeleccionados.map(a => (
                <Grid item xs={12} key={a.codigo}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
                    <Typography variant="body2">{a.label}</Typography>
                    <Typography variant="body2">+{fmtUSD(a.precio)}/mes</Typography>
                  </Box>
                </Grid>
              ))}
            </>
          )}

          {/* Totales */}
          <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Total mensual</Typography>
              <Typography variant="body2" fontWeight="bold">{fmtUSD(totalMensual)}/mes</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Total anual (×10)</Typography>
              <Typography variant="body2" fontWeight="bold">{fmtUSD(totalAnual)}/año</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Condiciones de la propuesta ───────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
          ⚙️ Condiciones
        </Typography>

        <Grid container spacing={2}>
          {/* Ciclo de facturación */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Ciclo de facturación
            </Typography>
            <ToggleButtonGroup
              value={ciclo}
              exclusive
              onChange={(_, v) => { if (v) setCiclo(v); }}
              color="primary"
              size="small"
            >
              <ToggleButton value="mensual" sx={{ fontWeight: 'bold' }}>
                Mensual — {fmtUSD(totalMensual)}/mes
              </ToggleButton>
              <ToggleButton value="anual" sx={{ fontWeight: 'bold' }}>
                Anual — {fmtUSD(totalAnual)}/año
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* Descuento */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de descuento</InputLabel>
              <Select
                value={descuentoTipo}
                label="Tipo de descuento"
                onChange={(e) => { setDescuentoTipo(e.target.value); setDescuentoValor(''); }}
              >
                <MenuItem value="porcentaje">Porcentaje (%)</MenuItem>
                <MenuItem value="monto_fijo">Monto fijo ($)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small"
              label={descuentoTipo === 'porcentaje' ? 'Descuento (%)' : 'Descuento ($)'}
              type="number"
              inputProps={{ min: 0, max: descuentoTipo === 'porcentaje' ? 100 : undefined, step: 0.01 }}
              value={descuentoValor}
              onChange={(e) => setDescuentoValor(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small"
              label="Descuento calculado"
              value={descuentoMonto > 0 ? `-${fmtUSD(descuentoMonto)}` : '—'}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Fecha de vencimiento */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small"
              label="Fecha de vencimiento de la propuesta"
              type="date"
              value={fechaVencimiento}
              onChange={(e) => setFechaVencimiento(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Total final destacado */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 1.5, bgcolor: '#06b6d4', color: '#fff', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Total {ciclo}{descuentoMonto > 0 ? ' con descuento' : ''}
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {fmtUSD(totalConDescuento)}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {ciclo === 'anual' ? '/año' : '/mes'}
              </Typography>
            </Paper>
          </Grid>

          {/* Notas */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notas de la propuesta"
              multiline rows={3}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Condiciones especiales, detalles de implementación, SLA, etc."
            />
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant="contained"
        fullWidth
        startIcon={<SendIcon />}
        onClick={handleGuardar}
        disabled={guardando || !planInteres}
        sx={{ py: 1.5, fontWeight: 'bold', bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}
      >
        {guardando ? 'Guardando...' : 'Enviar Propuesta SaaS'}
      </Button>
    </Box>
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const swalStyles = `
  .swal2-container {
    z-index: 99999 !important;
  }
`;

const PropuestaBuilder = ({ oportunidad, onGuardado, onTransicionCompleta }) => {
  const esSaas      = oportunidad?.tipo_negocio === 'saas';
  const esObraCivil = oportunidad?.tipo_negocio === 'obra_civil';
  const esServicios = oportunidad?.tipo_negocio === 'servicios';

  // ── Estado del builder clásico ─────────────────────────────────────────────
  const [lineas, setLineas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [partidas, setPartidas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!esSaas);
  const [materiales, setMateriales] = useState([]);
  const [tabActivo, setTabActivo] = useState(0);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = swalStyles;
    document.head.appendChild(styleSheet);
    return () => { document.head.removeChild(styleSheet); };
  }, []);

  useEffect(() => {
    if (esSaas) return; // No cargar catálogos para SaaS
    cargarCatalogos();
    if (esServicios) cargarInventario();
  }, [esSaas, esServicios]);

  const cargarCatalogos = async () => {
    try {
      setLoading(true);
      const partidasRes = await cargarPartidasEstandar();
      const catsRes = await cargarCategoriasPartida();
      setPartidas(partidasRes.data.data || partidasRes.data || []);
      setCategorias(catsRes.data.data || catsRes.data || []);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarInventario = async () => {
    try {
      const res = await cargarMaterialesInventario({ disponible_venta: true });
      const data = res.data.data || res.data || [];
      setMateriales(data.filter(m => m.disponible_venta === true && (m.precio_venta_crm > 0 || m.precio_venta > 0)));
    } catch (err) {
      console.error('Error cargando inventario:', err);
      toast.warning('No se pudo cargar el inventario');
    }
  };

  // ── Ruteo temprano para SaaS ───────────────────────────────────────────────
  if (esSaas) {
    return (
      <PropuestaSaas
        oportunidad={oportunidad}
        onGuardado={onGuardado}
        onTransicionCompleta={onTransicionCompleta}
      />
    );
  }

  // ── Builder clásico (obra_civil / servicios) ───────────────────────────────
  const añadirLinea = (item, tipo = 'partida') => {
    let precioUnitario = 0;
    if (tipo === 'material') {
      precioUnitario = parseFloat(item.precio_venta_crm) || parseFloat(item.precio_venta) || parseFloat(item.costo_promedio) || 0;
    } else {
      precioUnitario = parseFloat(item.precio_unitario) || 0;
    }

    const nueva = {
      id: Date.now(),
      tipo,
      partida_id: tipo === 'partida' ? item.id : null,
      material_id: tipo === 'material' ? item.id : null,
      codigo: item.codigo || item.sku || item.id,
      nombre: item.nombre,
      descripcion: item.descripcion || '',
      cantidad: 1,
      unidad: item.unidad || 'und',
      precio_unitario: precioUnitario,
      subtotal: precioUnitario,
      stock_disponible: tipo === 'material' ? (parseFloat(item.stock_actual) || 0) : null,
      afecta_inventario: tipo === 'material',
      tipo_precio: tipo === 'material' ? (item.precio_venta_crm ? 'crm' : 'pos') : null
    };

    setLineas(prev => [...prev, nueva]);
  };

  const actualizarLinea = (id, campo, valor) => {
    setLineas(prevLineas => prevLineas.map(l => {
      if (l.id !== id) return l;
      const valorNumerico = ['cantidad', 'precio_unitario'].includes(campo)
        ? (parseFloat(valor) || 0)
        : valor;
      const act = { ...l, [campo]: valorNumerico };
      if (['cantidad', 'precio_unitario'].includes(campo)) {
        const cantidad = campo === 'cantidad' ? valorNumerico : (parseFloat(l.cantidad) || 0);
        const precio   = campo === 'precio_unitario' ? valorNumerico : (parseFloat(l.precio_unitario) || 0);
        act.subtotal = cantidad * precio;
      }
      return act;
    }));
  };

  const lineasPartidas   = lineas.filter(l => l.tipo === 'partida');
  const lineasMateriales = lineas.filter(l => l.tipo === 'material');
  const subtotal         = lineas.reduce((acc, l) => acc + (parseFloat(l.subtotal) || 0), 0);

  const valorEstimado    = oportunidad.valor_estimado || 0;
  const totalCotizacion  = Math.round(subtotal * 1.18 * 100) / 100;
  const diferencia       = valorEstimado > 0 ? ((totalCotizacion - valorEstimado) / valorEstimado) * 100 : 0;
  const minimoAceptable  = valorEstimado * 0.70;
  const maximoAceptable  = valorEstimado * 1.30;

  const handleGuardar = async () => {
    if (lineas.length === 0) return toast.warning("Agrega partidas o materiales a la propuesta");

    if (esObraCivil && lineasMateriales.length > 0) {
      toast.error("❌ En Obra Civil no puedes agregar materiales del inventario. Usa solo partidas estándar.");
      return;
    }

    if (esServicios && lineasMateriales.length > 0) {
      const sinStock = lineasMateriales.filter(l => l.cantidad > (l.stock_disponible || 0));
      if (sinStock.length > 0) {
        const result = await Swal.fire({
          title: '⚠️ Stock insuficiente',
          html: `
            <div style="text-align: left;">
              <p>Los siguientes materiales exceden el stock disponible:</p>
              <ul>
                ${sinStock.map(l => `<li><strong>${l.nombre}</strong>: Solicita ${l.cantidad}, Disponible ${l.stock_disponible || 0}</li>`).join('')}
              </ul>
              <p style="font-size: 0.9em; color: #666;">¿Deseas continuar? Se generará una orden de compra pendiente.</p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continuar',
          cancelButtonText: 'Revisar',
          confirmButtonColor: '#f59e0b',
          backdrop: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        if (!result.isConfirmed) return;
      }
    }

    if (Math.abs(diferencia) > 30) {
      const result = await Swal.fire({
        title: '⚠️ Diferencia significativa detectada',
        html: `
          <div style="text-align: left;">
            <p><strong>Valor Estimado:</strong> $${valorEstimado.toLocaleString('es-DO')}</p>
            <p><strong>Cotización Total:</strong> $${totalCotizacion.toLocaleString('es-DO')}</p>
            <p style="color: ${diferencia < 0 ? '#ef4444' : '#f59e0b'}; font-weight: bold;">
              <strong>Diferencia:</strong> ${diferencia > 0 ? '+' : ''}${diferencia.toFixed(1)}%
            </p>
            <hr>
            <p style="font-size: 0.9em; color: #666;">
              ${diferencia < 0
                ? '⚠️ La cotización es significativamente menor. ¿Faltan partidas?'
                : '📈 La cotización es significativamente mayor. ¿Hay extras o el estimado era bajo?'}
            </p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, guardar de todos modos',
        cancelButtonText: 'Cancelar y revisar',
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6b7280',
        width: 500
      });
      if (!result.isConfirmed) return;
    }

    if (totalCotizacion < minimoAceptable) {
      Swal.fire({
        title: '❌ Cotización muy baja',
        html: `
          <p>La cotización <strong>$${totalCotizacion.toLocaleString('es-DO')}</strong> está muy por debajo del valor estimado.</p>
          <hr>
          <p><strong>Mínimo aceptable:</strong> $${minimoAceptable.toLocaleString('es-DO')} (70% del estimado)</p>
          <p style="color: #666; font-size: 0.9em;">Revisa que no falten partidas importantes.</p>
        `,
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444'
      });
      return;
    }

    if (totalCotizacion > maximoAceptable) {
      Swal.fire({
        title: '❌ Cotización muy alta',
        html: `
          <p>La cotización <strong>$${totalCotizacion.toLocaleString('es-DO')}</strong> está muy por encima del valor estimado.</p>
          <hr>
          <p><strong>Máximo aceptable:</strong> $${maximoAceptable.toLocaleString('es-DO')} (130% del estimado)</p>
          <p style="color: #666; font-size: 0.9em;">Revisa los precios unitarios o actualiza el valor estimado.</p>
        `,
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#ef4444'
      });
      return;
    }

    setGuardando(true);
    try {
      if (onGuardado) {
        const subtotalRedondeado = Math.round(subtotal * 100) / 100;
        const itbisCalculado     = Math.round(subtotalRedondeado * 0.18 * 100) / 100;
        const totalCalculado     = subtotalRedondeado + itbisCalculado;

        const payload = {
          oportunidadId: oportunidad.id,
          propuesta: esObraCivil ? {
            lineas: lineasPartidas.map(l => ({
              partida_id: l.partida_id,
              codigo: l.codigo,
              nombre: l.nombre,
              descripcion: l.descripcion,
              unidad: l.unidad || 'und',
              cantidad: Number(l.cantidad),
              precio_unitario: Number(l.precio_unitario),
              total: Number(l.subtotal)
            })),
            subtotal: subtotalRedondeado,
            impuestos: itbisCalculado,
            total: totalCalculado,
            version: (oportunidad.propuesta?.version || 0) + 1,
            fecha_envio: new Date(),
            estado_envio: 'enviado',
            tipo_cotizacion: 'obra_civil'
          } : {
            lineas_estandar: lineasPartidas.map(l => ({
              partida_id: l.partida_id,
              codigo: l.codigo,
              nombre: l.nombre,
              descripcion: l.descripcion,
              unidad: l.unidad || 'und',
              cantidad: Number(l.cantidad),
              precio_unitario: Number(l.precio_unitario),
              total: Number(l.subtotal)
            })),
            materiales_inventario: lineasMateriales.map(l => ({
              material_id: l.material_id,
              codigo: l.codigo,
              nombre: l.nombre,
              descripcion: l.descripcion,
              unidad: l.unidad || 'und',
              cantidad: Number(l.cantidad),
              precio_unitario: Number(l.precio_unitario),
              total: Number(l.subtotal),
              stock_disponible: l.stock_disponible,
              afecta_inventario: true
            })),
            subtotal: subtotalRedondeado,
            impuestos: itbisCalculado,
            total: totalCalculado,
            version: (oportunidad.propuesta?.version || 0) + 1,
            fecha_envio: new Date(),
            estado_envio: 'enviado',
            tipo_cotizacion: 'servicios'
          }
        };

        await onGuardado(payload);
      }

      toast.success(esObraCivil
        ? "✅ Propuesta de obra civil guardada"
        : "✅ Propuesta de servicios con materiales guardada"
      );

      if (onTransicionCompleta) onTransicionCompleta();
    } catch (error) {
      console.error('❌ Error en guardado de propuesta:', error);
      toast.error(error.response?.data?.error || 'Error al guardar la propuesta');
    } finally {
      setGuardando(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}>Cargando catálogos...</Box>;
  if (error)   return <Box sx={{ p: 3 }}>Error: {error}</Box>;

  const partidasFiltradas = categoriaSeleccionada
    ? partidas.filter(p => p.CategoriaPartidaId === categoriaSeleccionada || p.categoria_id === categoriaSeleccionada)
    : partidas;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
        🗂️ Propuesta Técnica: {oportunidad.prospecto?.nombre || oportunidad.prospecto?.empresa}
      </Typography>

      <Chip
        icon={esObraCivil ? <BuildIcon /> : <InventoryIcon />}
        label={esObraCivil ? 'Obra Civil - Solo catálogo de partidas' : 'Servicios - Catálogo + Inventario'}
        color={esObraCivil ? 'primary' : 'secondary'}
        sx={{ mb: 2 }}
      />

      {lineasPartidas.length > 0 && valorEstimado > 0 && (
        <Alert
          severity={Math.abs(diferencia) > 30 ? "error" : Math.abs(diferencia) > 20 ? "warning" : "info"}
          icon={diferencia < -20 ? <TrendingDownIcon /> : diferencia > 20 ? <TrendingUpIcon /> : <WarningIcon />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              {Math.abs(diferencia) > 30 ? (
                <Typography variant="body2" fontWeight="bold">
                  ⚠️ Diferencia significativa: {diferencia > 0 ? '+' : ''}{diferencia.toFixed(1)}%
                </Typography>
              ) : Math.abs(diferencia) > 20 ? (
                <Typography variant="body2">
                  ⚠️ Diferencia notable: {diferencia > 0 ? '+' : ''}{diferencia.toFixed(1)}%
                </Typography>
              ) : (
                <Typography variant="body2">
                  ✅ Dentro del rango esperado: {diferencia > 0 ? '+' : ''}{diferencia.toFixed(1)}%
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Estimado: ${valorEstimado.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})} •
                Cotizado: ${totalCotizacion.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </Typography>
            </Box>
            <Chip
              label={`${diferencia > 0 ? '+' : ''}${diferencia.toFixed(1)}%`}
              color={Math.abs(diferencia) > 30 ? "error" : Math.abs(diferencia) > 20 ? "warning" : "success"}
              size="small"
            />
          </Box>
        </Alert>
      )}

      {esServicios && (
        <Tabs
          value={tabActivo}
          onChange={(e, v) => setTabActivo(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<BuildIcon />} label={`Partidas Estándar (${lineasPartidas.length})`} />
          <Tab icon={<InventoryIcon />} label={`Materiales Inventario (${lineasMateriales.length})`} />
        </Tabs>
      )}

      {(esObraCivil || tabActivo === 0) && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f1f5f9' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            📋 {esObraCivil ? 'Seleccionar Partidas del Catálogo' : 'Partidas Estándar'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filtrar por Categoría</InputLabel>
              <Select
                value={categoriaSeleccionada || ''}
                onChange={(e) => setCategoriaSeleccionada(e.target.value || null)}
                label="Filtrar por Categoría"
              >
                <MenuItem value=""><em>Todas ({partidas.length})</em></MenuItem>
                {categorias.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nombre} ({partidas.filter(p => (p.CategoriaPartidaId || p.categoria_id) === cat.id).length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              fullWidth
              options={partidasFiltradas}
              getOptionLabel={(opt) => `[${opt.codigo || 'N/A'}] ${opt.nombre} - ${opt.unidad || 'UND'}`}
              onChange={(e, val) => { if (val) añadirLinea(val, 'partida'); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Buscar Partida (${partidasFiltradas.length} disponibles)`}
                  placeholder="Escribe para buscar..."
                />
              )}
              disabled={partidasFiltradas.length === 0}
            />
          </Box>
        </Paper>
      )}

      {esServicios && tabActivo === 1 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fef3c7' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#92400e' }}>
            📦 Materiales del Inventario
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Estos materiales afectarán el inventario cuando se emita la factura
          </Typography>
          <Autocomplete
            fullWidth
            options={materiales}
            getOptionLabel={(opt) => {
              const precio = parseFloat(opt.precio_venta_crm) || parseFloat(opt.precio_venta) || 0;
              return `[${opt.codigo || 'N/A'}] ${opt.nombre} - Stock: ${opt.stock_actual || 0} - RD$${precio.toFixed(2)}`;
            }}
            onChange={(e, val) => {
              if (val) {
                añadirLinea({
                  ...val,
                  precio_venta_crm: parseFloat(val.precio_venta_crm) || 0,
                  precio_venta: parseFloat(val.precio_venta) || 0
                }, 'material');
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Buscar Material (${materiales.length} disponibles)`}
                placeholder="Escribe para buscar en inventario..."
                helperText="Solo se muestran materiales marcados como 'Disponible para venta'"
              />
            )}
            disabled={materiales.length === 0}
            noOptionsText="No hay materiales disponibles para venta"
          />
        </Paper>
      )}

      {lineas.length > 0 && (
        <>
          {esServicios && (
            <Box sx={{ mb: 2 }}>
              <Chip label={`${lineasPartidas.length} partidas estándar`} color="primary" size="small" sx={{ mr: 1 }} />
              <Chip label={`${lineasMateriales.length} materiales de inventario`} color="secondary" size="small" />
            </Box>
          )}
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineas.map((linea) => (
                <TableRow key={linea.id}>
                  <TableCell>
                    <Chip
                      label={linea.tipo === 'partida' ? 'Partida' : 'Material'}
                      color={linea.tipo === 'partida' ? 'primary' : 'secondary'}
                      size="small" variant="outlined"
                    />
                  </TableCell>
                  <TableCell><Typography variant="caption">{linea.codigo}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{linea.nombre}</Typography>
                    {linea.tipo === 'material' && (
                      <Typography variant="caption" color="text.secondary">
                        Stock disponible: {linea.stock_disponible || 0}
                      </Typography>
                    )}
                    <TextField
                      fullWidth multiline variant="standard" size="small"
                      value={linea.descripcion}
                      onChange={(e) => actualizarLinea(linea.id, 'descripcion', e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <input
                      type="number" style={{ width: 60 }}
                      value={linea.cantidad}
                      onChange={(e) => actualizarLinea(linea.id, 'cantidad', parseFloat(e.target.value) || 0)}
                    />
                    <Typography variant="caption" display="block">{linea.unidad}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <input
                      type="number" style={{ width: 80 }}
                      value={linea.precio_unitario}
                      onChange={(e) => actualizarLinea(linea.id, 'precio_unitario', parseFloat(e.target.value) || 0)}
                    />
                  </TableCell>
                  <TableCell align="right">${linea.subtotal.toLocaleString()}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => setLineas(lineas.filter(l => l.id !== linea.id))} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Box sx={{ textAlign: 'right', p: 2, bgcolor: '#f8fafc' }}>
        <Typography variant="body2" color="text.secondary">
          Subtotal: RD${subtotal.toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ITBIS (18%): RD${(subtotal * 0.18).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </Typography>
        <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
          Total: RD${(subtotal * 1.18).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            sx={{ bgcolor: esObraCivil ? '#3b82f6' : '#8b5cf6' }}
            onClick={handleGuardar}
            disabled={guardando || lineas.length === 0}
          >
            {guardando ? 'Guardando...' : esObraCivil ? 'Enviar Propuesta de Obra' : 'Enviar Propuesta de Servicios'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PropuestaBuilder;
